import { forwardRef, useState, useCallback } from 'react';
import ReactSelect, { Props as SelectProps, components } from 'react-select';
import { AsyncPaginate, LoadOptions } from 'react-select-async-paginate';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

interface Option {
  value: string;
  label: string;
  isDisabled?: boolean;
  [key: string]: any; // Allow additional properties
}

interface AsyncLoadConfig {
  loadOptions: (search: string, page: number) => Promise<{
    options: Option[];
    hasMore: boolean;
    total?: number;
  }>;
  pageSize?: number;
  debounceTimeout?: number;
  cacheOptions?: boolean;
}

interface CustomSelectProps extends Omit<SelectProps, 'options'> {
  label?: string;
  error?: string;
  helperText?: string;
  options?: Option[];
  isSearchable?: boolean;
  isClearable?: boolean;
  usePortal?: boolean;
  // New async props
  async?: boolean;
  asyncConfig?: AsyncLoadConfig;
}

const DropdownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <ChevronDownIcon className="w-4 h-4 text-gray-400" />
    </components.DropdownIndicator>
  );
};

const getCustomStyles = (error: boolean) => ({
  control: (provided: any, state: any) => ({
    ...provided,
    border: state.isFocused ? '1px solid #3b82f6' : '1px solid #d1d5db',
    borderRadius: '0.5rem',
    padding: '0 !important',
    minHeight: '48px !important',
    height: '48px !important',
    backgroundColor: 'white',
    boxShadow: 'none',
    cursor: 'text',
    '&:hover': {
      borderColor: state.isFocused ? '#3b82f6' : '#9ca3af',
    },
    transition: 'all 0.2s ease',
    borderColor: error 
      ? (state.isFocused ? '#ef4444' : '#fca5a5')
      : (state.isFocused ? '#3b82f6' : '#d1d5db'),
    zIndex: 10,
    position: 'relative',
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: state.isSelected 
      ? '#3b82f6' 
      : state.isFocused 
        ? '#f3f4f6' 
        : 'white',
    color: state.isSelected ? 'white' : '#374151',
    padding: '10px 12px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: state.isSelected ? '#3b82f6' : '#f3f4f6',
    },
  }),
  menu: (provided: any) => ({
    ...provided,
    borderRadius: '0.5rem',
    backgroundColor: 'white',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    border: '1px solid #e5e7eb',
    marginTop: '4px',
    zIndex: 9999,
  }),
  menuList: (provided: any) => ({
    ...provided,
    padding: '4px 0',
    maxHeight: '200px',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
  }),
  menuPortal: (provided: any) => ({
    ...provided,
    zIndex: 9999,
  }),
  placeholder: (provided: any) => ({
    ...provided,
    color: 'transparent',
    position: 'absolute',
    top: '50%',
    left: '12px',
    transform: 'translateY(-50%)',
    margin: 0,
    padding: 0,
  }),
  noOptionsMessage: (provided: any) => ({
    ...provided,
    color: '#6b7280',
    fontSize: '14px',
    padding: '10px 12px',
  }),
  singleValue: (provided: any, state: any) => ({
    ...provided,
    color: '#374151',
    margin: '0 !important',
    padding: '0 !important',
    display: 'flex !important',
    alignItems: 'center !important',
    height: '48px !important',
    lineHeight: '48px !important',
    opacity: state.hasValue && state.selectProps.inputValue ? 0 : 1,
    transform: state.hasValue && state.selectProps.inputValue ? 'translateX(-100%)' : 'translateX(0)',
    transition: 'opacity 150ms, transform 150ms',
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    padding: '0 12px !important',
    margin: '0 !important',
    display: 'flex !important',
    alignItems: 'center !important',
    height: '48px !important',
    minHeight: '48px !important',
    position: 'relative !important',
  }),
  indicatorsContainer: (provided: any) => ({
    ...provided,
    height: '48px',
  }),
  input: (provided: any) => ({
    ...provided,
    margin: '0 !important',
    padding: '0 !important',
    color: '#374151 !important',
    fontSize: '16px !important',
    lineHeight: '48px !important',
    height: '48px !important',
    opacity: 1,
    background: 'transparent',
    border: 'none',
    outline: 'none',
  }),
});

const Select = forwardRef<any, CustomSelectProps>(({
  label,
  error,
  helperText,
  options = [],
  isSearchable = false,
  isClearable = false,
  usePortal = true,
  className = '',
  async = false,
  asyncConfig,
  ...props
}, ref) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleChange = (selectedOption: any, actionMeta: any) => {
    // Pass the full option object for backward compatibility
    props.onChange?.(selectedOption, actionMeta);
  };

  // Convert value to option object for ReactSelect
  const getSelectedOption = () => {
    if (!props.value) return null;
    
    // If value is already an option object, return it
    if (typeof props.value === 'object' && props.value !== null) {
      const valueObj = props.value as any;
      if (valueObj.value && valueObj.label) {
        return valueObj;
      }
    }
    
    // Otherwise, find the option by value string
    const valueString = typeof props.value === 'string' ? props.value : String(props.value);
    return options.find(option => option.value === valueString) || null;
  };

  // Async load wrapper for AsyncPaginate
  const loadAsyncOptions: LoadOptions<Option, any, any> = useCallback(async (search: string, loadedOptions: any, { page }: any) => {
    if (!asyncConfig?.loadOptions) {
      return { options: [], hasMore: false };
    }
    
    try {
      const result = await asyncConfig.loadOptions(search, page || 1);
      return {
        options: result.options,
        hasMore: result.hasMore,
        additional: {
          page: (page || 1) + 1
        }
      };
    } catch (error) {
      console.error('Error loading options:', error);
      return { options: [], hasMore: false };
    }
  }, [asyncConfig]);

  // Check if there's a current value - more reliable than state
  const hasValue = Boolean(props.value);
  const isLabelFloating = isFocused || hasValue;

  const commonProps = {
    ref,
    styles: getCustomStyles(Boolean(error)),
    components: { DropdownIndicator },
    className,
    classNamePrefix: "react-select",
    onFocus: handleFocus,
    onBlur: handleBlur,
    onChange: handleChange,
    placeholder: "",
    isSearchable,
    isClearable,
    noOptionsMessage: ({ inputValue }: any) => 
      inputValue && async ? `No results found for "${inputValue}"` : "No options found",
    loadingMessage: () => "Loading...",
    menuPortalTarget: usePortal ? document.body : undefined,
  };

  return (
    <div className="w-full">
      <div className="relative">
        {async && asyncConfig ? (
          <AsyncPaginate
            {...commonProps}
            value={getSelectedOption()}
            loadOptions={loadAsyncOptions}
            debounceTimeout={asyncConfig.debounceTimeout || 300}
            cacheUniqs={[props.value]}
            additional={{ page: 1 }}
            {...Object.fromEntries(Object.entries(props).filter(([key]) => 
              !['value', 'onChange', 'options', 'async', 'asyncConfig'].includes(key)
            ))}
          />
        ) : (
          <ReactSelect
            {...commonProps}
            options={options}
            value={getSelectedOption()}
            {...Object.fromEntries(Object.entries(props).filter(([key]) => 
              !['value', 'onChange', 'async', 'asyncConfig'].includes(key)
            ))}
          />
        )}
        
        {label && (
          <label
            className={`
              absolute left-4 transition-all duration-200 pointer-events-none z-20
              ${isLabelFloating
                ? '-top-2.5 text-xs text-gray-500 bg-white px-2'
                : 'top-1/2 -translate-y-1/2 text-base text-gray-400'
              }
              ${error && isLabelFloating ? 'text-red-500' : ''}
              ${isFocused && !error ? 'text-blue-500' : ''}
            `}
          >
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
      </div>
      
      {(error || helperText) && (
        <div className={`mt-2 text-sm ${error ? 'text-red-600' : 'text-gray-500'}`}>
          {error || helperText}
        </div>
      )}
    </div>
  );
});

Select.displayName = 'Select';

export default Select;